import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getWhatsAppQR, startWhatsAppSession, WhatsAppQRResponse } from '../api/whatsapp';
import { useQueryClient } from '@tanstack/react-query';

type Props = {
  onClose: () => void;
};

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 60; // 3 min timeout

const WhatsAppQRModal: React.FC<Props> = ({ onClose }) => {
  const [qrData, setQrData] = useState<WhatsAppQRResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollCount = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const queryClient = useQueryClient();

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleConnected = useCallback(() => {
    stopPolling();
    queryClient.invalidateQueries({ queryKey: ['user'] });
    onClose();
  }, [stopPolling, queryClient, onClose]);

  const poll = useCallback(async () => {
    pollCount.current += 1;
    if (pollCount.current > MAX_POLLS) {
      stopPolling();
      setError('Connection timed out. Please try again.');
      return;
    }

    try {
      const data = await getWhatsAppQR();
      setQrData(data);

      if (data.status === 'open') {
        handleConnected();
      }
    } catch {
      // ignore transient errors; keep polling
    }
  }, [stopPolling, handleConnected]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const data = await startWhatsAppSession();
        if (cancelled) return;

        setQrData(data);

        if (data.status === 'open') {
          handleConnected();
          return;
        }

        timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setError('Failed to start WhatsApp session. Please try again.');
      }
    };

    init();

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [poll, stopPolling, handleConnected]);

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/70'
      onClick={onClose}
    >
      <div
        className='relative flex w-[320px] flex-col items-center gap-4 rounded-2xl bg-[#0d1117] p-6 shadow-xl'
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className='absolute right-4 top-4 text-gray-400 hover:text-white'
          aria-label='Close'
        >
          <i className='fas fa-times text-lg' />
        </button>

        <h2 className='text-lg font-bold text-white'>Connect WhatsApp</h2>

        {error ? (
          <p className='text-center text-sm text-red-400'>{error}</p>
        ) : qrData?.status === 'open' ? (
          <p className='text-center text-sm text-green-400'>Connected!</p>
        ) : qrData?.qr ? (
          <>
            <p className='text-center text-xs text-gray-400'>
              Open WhatsApp on your phone, go to{' '}
              <strong className='text-white'>Linked Devices</strong>, and scan this code.
            </p>
            <img
              src={qrData.qr}
              alt='WhatsApp QR code'
              className='h-52 w-52 rounded-lg bg-white p-1'
            />
            <p className='text-center text-xs text-gray-500'>
              Waiting for scan…
            </p>
          </>
        ) : (
          <div className='flex flex-col items-center gap-2'>
            <i className='fas fa-spinner animate-spin text-2xl text-green-400' />
            <p className='text-sm text-gray-400'>Generating QR code…</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppQRModal;
